import { Button, Dialog, toast } from '@neo4j-ndl/react';
import { fleetManagementApi, useActiveProject } from '@nx/state';
import { URLs, isNonEmptyString, isNullish } from '@nx/stdlib';
import { ConnectionUrlInput } from '@nx/ui/src/connection-modal/connection-url-input';
import { useForm, useWatch } from 'react-hook-form';

interface UpdateConnectionUrlFormValues {
  connectionUrl: string;
}

type UpdateConnectionUrlModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  deploymentId: string;
  currentConnectionUrl: string | undefined;
};

export const validateSecureProtocol = (value: string): boolean | string => {
  if (URLs.Neo4jURL.isValid(value)) {
    return true;
  }
  return 'Please use a valid URL';
};

export const UpdateConnectionUrlModal = ({
  isOpen,
  closeModal,
  deploymentId,
  currentConnectionUrl,
}: UpdateConnectionUrlModalProps) => {
  const activeProject = useActiveProject();
  const [updateConnectionUrl, { isLoading }] = fleetManagementApi.useUpdateConnectionUrlMutation();
  const { handleSubmit, register, control, formState, setValue } = useForm<UpdateConnectionUrlFormValues>({
    defaultValues: {
      connectionUrl: currentConnectionUrl ?? '',
    },
    mode: 'onChange',
  });

  const connectionUrlField = register('connectionUrl', {
    required: true,
    validate: (value) => {
      if (value === currentConnectionUrl) {
        return false;
      }
      if (isNonEmptyString(value)) {
        return validateSecureProtocol(value);
      }
      return true;
    },
  });
  const connectionUrl = useWatch({ control, name: connectionUrlField.name });

  const isUpdate = isNonEmptyString(currentConnectionUrl);

  const onSubmit = (data: UpdateConnectionUrlFormValues) => {
    if (isNullish(data.connectionUrl)) {
      return;
    }
    void updateConnectionUrl({
      projectId: activeProject.id,
      deploymentId,
      connectionUrl: data.connectionUrl,
    })
      .then(({ error }) => {
        if (error) {
          toast.danger(isUpdate ? 'Failed to update connection URL' : 'Failed to add connection URL', {
            shouldAutoClose: true,
          });
        } else {
          toast.success(isUpdate ? 'Connection URL updated' : 'Connection URL added', {
            shouldAutoClose: true,
          });
          closeModal();
        }
      })
      .catch((err: Error) => {
        toast.danger(err.message);
      });
  };

  return (
    <Dialog size="small" isOpen={isOpen} onClose={closeModal}>
      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
      >
        <Dialog.Header>{isUpdate ? 'Update connection URL' : 'Add connection URL'}</Dialog.Header>
        <Dialog.Content>
          <ConnectionUrlInput
            {...connectionUrlField}
            value={connectionUrl}
            onValueChange={(value) => {
              setValue(connectionUrlField.name, value);
            }}
            errorText={formState.errors.connectionUrl?.message}
          />
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            fill="outlined"
            color="neutral"
            onClick={() => {
              closeModal();
            }}
          >
            Cancel
          </Button>
          <Button fill="filled" color="primary" type="submit" isLoading={isLoading} isDisabled={!formState.isValid}>
            {isUpdate ? 'Update' : 'Add'}
          </Button>
        </Dialog.Actions>
      </form>
    </Dialog>
  );
};
